import React, { ReactElement } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Paths } from '../paths';
import { useAuth } from 'services/auth/hooks';
import { userDataErrorAtom } from 'services/auth/atoms';
import { Loading } from 'components/Loading/Loading';
import { userAtom } from 'services/user/atoms';
import { useRecoilValue } from 'recoil';
import { Box, Stack, SxProps, Typography } from '@mui/material';
import { Button } from 'system/atoms';
import { organizationAtom } from 'services/organization/atom';
import { organizationGroupOrganizationsAtom } from 'services/organizationGroupOrganizations/atom';
import { Header } from 'components/Header';
import {
  checkHasSubscriptionPlan,
  planMembershipAtom
} from 'services/billing/atoms';
import { withPrivateNavbar } from 'modules/layout/components/PrivateNavBar';
import { withEmbeddedNavbar } from 'modules/atsEmbed/components/EmbeddedNavBar';
import { getAtsEmbedContext } from 'modules/atsEmbed/utils/atsEmbedContext';
import {
  OrganizationGroupRole,
  SubscriptionProduct
} from 'generated-graphql/types';
import { RouterLink } from 'common/ui/RouterLink';
import { useOrganizationConfig } from 'core/stores/hooks/useOrganizationConfig';
import { useFlag } from 'services/flags/hooks';
import { Flags } from 'services/flags/constants';

interface PrivateRouteProps {
  element: ReactElement;
  isCampaign?: boolean;
  isAnalyse?: boolean;
  isInternalSourcing?: boolean;
  useLayoutContainer?: boolean;
  disablePadding?: boolean;
  hideNavbar?: boolean;
  // /ats replaces the selected sub-account, so its checks do not apply.
  skipOrganizationChecks?: boolean;
}

export const layoutContainerPaddingX: `${number}px` = '50px';
export const layoutContainerPaddingTop: `${number}px` = '20px';
export const layoutContainerPaddingBottom: `${number}px` = '100px';
export const layoutContainerPaddingY: `${number}px` = '20px';

// Header height for AppBar - Update if AppBar/Toolbar height changes
// Based on: AppBar padding (11px top + 11px bottom) + Toolbar height (~64px) = 86px
export const headerHeight = 86;

const layoutContainerSx: SxProps = {
  pt: layoutContainerPaddingTop,
  pb: layoutContainerPaddingBottom,
  px: layoutContainerPaddingX
};

const loadingContainerSx: SxProps = { mt: 3 };

const errorContainerSx: SxProps = { ...layoutContainerSx, gap: 4 };

export const PrivateRoute: React.FC<PrivateRouteProps> = ({
  element,
  isCampaign,
  isAnalyse,
  isInternalSourcing,
  useLayoutContainer,
  disablePadding = false,
  hideNavbar = false,
  skipOrganizationChecks = false
}) => {
  const { userAuth, isLoading, signOut } = useAuth();
  const isSignOutHidden = Boolean(useFlag(Flags.HIDE_SIGN_OUT_CONTROL));
  const user = useRecoilValue(userAtom);
  const userDataError = useRecoilValue(userDataErrorAtom);
  const location = useLocation();
  const organization = useRecoilValue(organizationAtom);
  const planMemberships = useRecoilValue(planMembershipAtom);
  const hasAnalysePlan = useRecoilValue(
    checkHasSubscriptionPlan(SubscriptionProduct.PoppScoring)
  );
  const hasCampaignPlan = useRecoilValue(
    checkHasSubscriptionPlan(SubscriptionProduct.PoppConversations)
  );
  const wrapWithNavbar = hideNavbar
    ? (page: ReactElement) => page
    : getAtsEmbedContext()
    ? withEmbeddedNavbar
    : withPrivateNavbar;
  const organizationConfig = useOrganizationConfig();
  const hasInternalSourcingEnabled =
    organizationConfig?.internalSourcingEnabled;

  const visibleOrganizations = useRecoilValue(
    organizationGroupOrganizationsAtom
  );
  const isOnboard = user?.isOnboard;

  if (isLoading)
    return (
      <Box sx={loadingContainerSx}>
        <Loading />
      </Box>
    );

  // Wait for the session check before redirecting.
  if (userAuth === undefined)
    return (
      <Box sx={loadingContainerSx}>
        <Loading />
      </Box>
    );

  if (!userAuth) {
    return <Navigate to={Paths.LOGIN} state={{ from: location }} replace />;
  }

  // The selected organisation's queries also raise userDataError; /ats is about
  // to replace that organisation, so only its own user data must have loaded.
  if (skipOrganizationChecks && user && visibleOrganizations !== null) {
    return wrapWithNavbar(element);
  }

  // The session is valid but its user data did not load. Every guard below
  // needs that data, so offer a retry rather than an endless spinner.
  if (userDataError) {
    return (
      <Stack sx={errorContainerSx}>
        <Header
          title="We could not load your account"
          subtitle="Your session is still active. This is usually temporary."
        />
        <Stack direction="row" gap={2}>
          <Button variant="primary" onClick={() => window.location.reload()}>
            Try again
          </Button>
          {!isSignOutHidden && (
            <Button variant="secondary" onClick={() => signOut()}>
              Sign Out
            </Button>
          )}
        </Stack>
      </Stack>
    );
  }

  if (skipOrganizationChecks) {
    // Email login sets the session before the user record loads.
    return (
      <Box sx={loadingContainerSx}>
        <Loading />
      </Box>
    );
  }

  const isOrgGroupAdmin =
    user?.organizationGroupRole ===
    OrganizationGroupRole.OrganizationGroupAdmin;

  const isAdminPath = location.pathname.startsWith(Paths.ADMINISTRATOR);

  // When all accounts are hidden, org group admins must be able to reach the
  // administrator page to unhide accounts. We detect this by checking:
  //   - visibleOrganizations is an empty array (loaded, but none visible)
  //   - organization is null (no org selected)
  const allAccountsHidden =
    visibleOrganizations !== null &&
    visibleOrganizations.length === 0 &&
    !organization;

  // Also handle the case where a selected org is not in the visible list
  // (e.g. stale from localStorage after an admin hid it). Redirect org group
  // admins to the administrator page; show a message for other users.
  const selectedOrgIsHidden =
    organization !== null &&
    visibleOrganizations !== null &&
    !visibleOrganizations.some((org) => org.id === organization.id);

  if (allAccountsHidden || selectedOrgIsHidden) {
    if (isOrgGroupAdmin) {
      if (isAdminPath) {
        return wrapWithNavbar(element);
      }
      return <Navigate to={Paths.ADMINISTRATOR_ACCOUNTS_OVERVIEW} replace />;
    }

    // Non-super-admin users cannot access the administrator page to unhide
    // accounts. Show them a message explaining the situation.
    return wrapWithNavbar(
      <Stack sx={{ ...layoutContainerSx, gap: 4 }}>
        <Header
          title="No available accounts"
          prevPageTitle="Accounts"
          subtitle="All accounts in your organisation have been hidden by an administrator."
        />
        <Stack gap={2}>
          <Typography variant="subtitle1">
            Please contact your administrator to restore access to your
            accounts.
          </Typography>
          <Stack direction="row" gap={2}>
            <RouterLink to={Paths.CONTACT_SUPPORT}>
              <Button variant="secondary">Contact Support</Button>
            </RouterLink>
            {!isSignOutHidden && (
              <Button variant="secondary" onClick={() => signOut()}>
                Sign Out
              </Button>
            )}
          </Stack>
        </Stack>
      </Stack>
    );
  }

  if (!organizationConfig && !!userAuth)
    return (
      <Box sx={loadingContainerSx}>
        <Loading />
      </Box>
    );

  if (!organization || !planMemberships) {
    return (
      <Box sx={loadingContainerSx}>
        <Loading />
      </Box>
    );
  }

  if (isOnboard === false && location.pathname !== Paths.ONBOARDING) {
    return <Navigate to={Paths.ONBOARDING} state={{ from: location }} />;
  }

  if (isAnalyse && !hasAnalysePlan) {
    return wrapWithNavbar(
      <Stack
        sx={{
          ...layoutContainerSx,
          gap: 4
        }}
      >
        <Header
          title="Analyse"
          prevPageTitle="Analyse"
          subtitle="Rank candidates by analysing their CV"
        />
        <Stack gap={2}>
          <Typography variant="subtitle1">
            You are not currently subscribed to campaigns, you may request
            access by clicking the button below.
          </Typography>
          <RouterLink to={Paths.CONTACT_SUPPORT}>
            <Button variant="secondary">Purchase Analyse</Button>
          </RouterLink>
        </Stack>
      </Stack>
    );
  }

  if (isInternalSourcing && !hasInternalSourcingEnabled) {
    return wrapWithNavbar(
      <Stack
        sx={{
          ...layoutContainerSx,
          gap: 4
        }}
      >
        <Header
          title="Internal Sourcing"
          prevPageTitle="Internal Sourcing"
          subtitle="Source candidates from your internal database"
        />
        <Stack gap={2}>
          <Typography variant="subtitle1">
            Internal sourcing is not enabled for your organization. Please
            contact support to request access.
          </Typography>
          <RouterLink to={Paths.CONTACT_SUPPORT}>
            <Button variant="secondary">Contact Support</Button>
          </RouterLink>
        </Stack>
      </Stack>
    );
  }

  if (isCampaign && !hasCampaignPlan) {
    return wrapWithNavbar(
      <Stack
        sx={{
          ...layoutContainerSx,
          gap: 4
        }}
      >
        <Stack gap={1}>
          <Typography variant="h3" fontWeight="bold" mt={2}>
            Campaigns
          </Typography>
          <Typography variant="subtitle1">
            Conversational agent to engage with candidates at scale
          </Typography>
        </Stack>
        <Stack gap={2}>
          <Typography variant="subtitle1">
            You are not currently subscribed to campaigns, you may request
            access by clicking the button below.
          </Typography>
          <RouterLink to={Paths.CONTACT_SUPPORT}>
            <Button variant="secondary">Purchase Campaigns</Button>
          </RouterLink>
        </Stack>
      </Stack>
    );
  }

  if (useLayoutContainer) {
    return wrapWithNavbar(
      <Box
        sx={
          disablePadding
            ? {
                ...layoutContainerSx,
                px: 0,
                py: 0
              }
            : layoutContainerSx
        }
      >
        {element}
      </Box>
    );
  }

  return wrapWithNavbar(element);
};
